import { useQuery } from '@tanstack/react-query';

import type { DtoV1BoltResponseModelsAssetsDepositoryStatementScreenDataDictionarySystemStringSystemObject } from '~/__generated__/api';
import { useUser } from '~/common/hooks/useAuthentication';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { useSelectedAlert } from '~/stores/navigationStore';

import { checkIfNextLargestDepositFirstEntry } from '../../AssetDepositoryAccounts/helpers';

export const keys = {
  all: ['getAssetBankStatementData'],
  forDocument: (assetId: number | string, documentId: number) => [
    ...keys.all,
    documentId,
    Number.parseInt(assetId as string),
  ],
};

function resolveDate(extractedValue: string, initialValue: string) {
  if (extractedValue !== 'Not Available' && !initialValue) {
    return extractedValue;
  }
  return initialValue || '';
}

const useGetAssetBankStatement = (documentId: number) => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const { assetId, displayTitle } = useSelectedAlert(
    ({ additionalReferences, displayTitle }) => ({
      assetId: additionalReferences.AssetId,
      displayTitle,
    }),
  );

  const loanStatus = useGetLoanSummary().data?.loanInfo?.loanStatus;

  return useQuery({
    queryKey: keys.forDocument(assetId, documentId),
    queryFn: async () => {
      const response = await typedApiClient.v1.statementGetStatementInformation(
        {
          obfuscatedLoanIdentifier,
          assetId: +assetId,
          documentId: documentId,
        },
      );

      const data =
        response.data as DtoV1BoltResponseModelsAssetsDepositoryStatementScreenDataDictionarySystemStringSystemObject & {
          screenData: Record<string, any>;
        };

      data.screenData.isUnderwriter = isUnderwriter;
      data.screenData.lastFourAccountNumber = data?.screenData?.account
        ?.accountNumber
        ? data?.screenData?.account?.accountNumber?.substring(
            data?.screenData?.account?.accountNumber?.length - 4,
          )
        : null;
      data.screenData.showIncomeDecreasedLargeDepositAlert =
        checkIfNextLargestDepositFirstEntry(
          data?.initialFormValues?.nextLargestDeposit,
          data?.screenData?.assetsCalculationInformation?.largeDepositThreshold,
        );
      data.screenData.debtsAndDeductionShowFieldArrayFlag =
        data?.initialFormValues?.debtsAndDeduction?.length > 0;
      data.screenData.largeDepositShowFieldArrayFlag =
        data?.initialFormValues?.largeDeposit?.length > 0;
      data.screenData.title = displayTitle?.replace('Bank ', '');
      data.screenData.loanStatus = loanStatus;
      data.initialFormValues.statementStartDate = resolveDate(
        data?.screenData?.extractedData?.startDate?.value,
        data?.initialFormValues?.statementStartDate,
      );
      data.initialFormValues.statementEndDate = resolveDate(
        data?.screenData?.extractedData?.endDate?.value,
        data?.initialFormValues?.statementEndDate,
      );
      data.screenData.bankNameOrAssetType =
        data?.screenData?.account?.bankName ||
        data?.screenData?.account?.assetType;

      return {
        ...data,
      };
    },

    cacheTime: 0,
  });
};

export default useGetAssetBankStatement;
